<!--
  - SPDX-FileCopyrightText: 2020 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<NcAppSettingsDialog
		id="conversation-settings-container"
		:aria-label="t('spreed', 'Conversation settings')"
		:name="t('spreed', 'Conversation settings')"
		:open="showSettings"
		showNavigation
		legacy
		noVersion
		@update:open="handleHideSettings">
		<NcAppSettingsSection
			id="basic-info"
			:name="t('spreed', 'Basic Info')">
			<BasicInfo
				:conversation="conversation"
				:canFullModerate="canFullModerate" />
		</NcAppSettingsSection>

		<template v-if="!isBreakoutRoom">
			<!-- Notifications settings and devices preview screen -->
			<NcAppSettingsSection
				v-if="!isNoteToSelf && !isOneToOneFormer"
				id="notifications"
				:name="t('spreed', 'Personal')">
				<NotificationsSettings v-if="!isGuest" :conversation="conversation" />
			</NcAppSettingsSection>

			<NcAppSettingsSection
				id="conversation-settings"
				:name="selfIsOwnerOrModerator ? t('spreed', 'Moderation') : t('spreed', 'Setup overview')">
				<ListableSettings v-if="!isNoteToSelf && !isGuest && !isOneToOne && !isClassified" :token="token" :canModerate="canFullModerate" />
				<MentionsSettings v-if="!isNoteToSelf && !isOneToOne" :token="token" :canModerate="canFullModerate" />
				<LinkShareSettings v-if="!isNoteToSelf && !isClassified" :token="token" :canModerate="canFullModerate" />
				<RecordingConsentSettings v-if="!isNoteToSelf && !isOneToOneFormer && recordingConsentAvailable" :token="token" :canModerate="selfIsOwnerOrModerator" />
				<ExpirationSettings v-if="!isOneToOneFormer && hasMessageExpirationFeature" :token="token" :canModerate="selfIsOwnerOrModerator" />
				<BanSettings v-if="supportBanV1 && canFullModerate" :token="token" />
			</NcAppSettingsSection>

			<!-- Meeting: lobby and sip -->
			<NcAppSettingsSection
				v-if="canFullModerate && !isNoteToSelf"
				id="meeting"
				:name="meetingHeader">
				<LobbySettings :token="token" />
				<SipSettings v-if="canUserEnableSIP" />
			</NcAppSettingsSection>

			<!-- Conversation permissions -->
			<NcAppSettingsSection
				v-if="canFullModerate && !isNoteToSelf"
				id="permissions"
				:name="t('spreed', 'Permissions')">
				<ConversationPermissionsSettings :token="token" />
			</NcAppSettingsSection>

			<!-- Live transcription -->
			<NcAppSettingsSection
				v-if="canConfigureLiveTranscription"
				id="live-transcription"
				:name="t('spreed', 'Live transcription')">
				<LiveTranscriptionSettings :token="token" />
			</NcAppSettingsSection>
			<NcAppSettingsSection
				v-else-if="hintLiveTranscription"
				id="live-transcription"
				:name="t('spreed', 'Live transcription')">
				<NcCheckboxRadioSwitch
					:description="t('spreed', 'Live transcriptions app is not installed')"
					type="switch"
					disabled>
					{{ t('spreed', 'Enable live transcription') }}
				</NcCheckboxRadioSwitch>
			</NcAppSettingsSection>

			<!-- Breakout rooms -->
			<NcAppSettingsSection
				v-if="canConfigureBreakoutRooms"
				id="breakout-rooms"
				:name="t('spreed', 'Breakout Rooms')">
				<BreakoutRoomsSettings :token="token" />
			</NcAppSettingsSection>

			<!-- Matterbridge settings -->
			<NcAppSettingsSection
				v-if="canFullModerate && matterbridgeEnabled && !isClassified"
				id="matterbridge"
				:name="t('spreed', 'Matterbridge')">
				<MatterbridgeSettings />
			</NcAppSettingsSection>

			<!-- Bots settings -->
			<NcAppSettingsSection
				v-if="selfIsOwnerOrModerator && supportBotsV1 && !isClassified"
				id="bots"
				:name="t('spreed', 'Bots')">
				<BotsSettings :token="token" />
			</NcAppSettingsSection>

			<!-- Destructive actions -->
			<NcAppSettingsSection
				v-if="canLeaveConversation || canDeleteConversation || showPreserveConversationSetting"
				id="dangerzone"
				:name="t('spreed', 'Danger zone')">
				<PreserveConversationSettings v-if="showPreserveConversationSetting" :token="token" />
				<LockingSettings v-if="canFullModerate && !isNoteToSelf" :token="token" />
				<template v-if="supportsArchive">
					<h4 class="app-settings-section__subtitle">
						{{ t('spreed', 'Archive conversation') }}
					</h4>
					<p class="app-settings-section__hint">
						{{ t('spreed', 'Archived conversations are hidden from the conversation list by default. However, they will still appear when you search for the conversation name or access a list of archived conversations.') }}
					</p>
					<NcCheckboxRadioSwitch
						type="switch"
						:modelValue="isArchived"
						@update:modelValue="toggleArchiveConversation">
						{{ t('spreed', 'Archive conversation') }}
					</NcCheckboxRadioSwitch>
				</template>
				<DangerZone
					:conversation="conversation"
					:canLeaveConversation="canLeaveConversation"
					:canDeleteConversation="canDeleteConversation" />
			</NcAppSettingsSection>
		</template>
	</NcAppSettingsDialog>
</template>

<script>
import { subscribe, unsubscribe } from '@nextcloud/event-bus'
import { t } from '@nextcloud/l10n'
import { ref } from 'vue'
import NcAppSettingsDialog from '@nextcloud/vue/components/NcAppSettingsDialog'
import NcAppSettingsSection from '@nextcloud/vue/components/NcAppSettingsSection'
import NcCheckboxRadioSwitch from '@nextcloud/vue/components/NcCheckboxRadioSwitch'
import BanSettings from './BanSettings/BanSettings.vue'
import BasicInfo from './BasicInfo.vue'
import BotsSettings from './BotsSettings.vue'
import BreakoutRoomsSettings from './BreakoutRoomsSettings.vue'
import ConversationPermissionsSettings from './ConversationPermissionsSettings.vue'
import DangerZone from './DangerZone.vue'
import ExpirationSettings from './ExpirationSettings.vue'
import LinkShareSettings from './LinkShareSettings.vue'
import ListableSettings from './ListableSettings.vue'
import LiveTranscriptionSettings from './LiveTranscriptionSettings.vue'
import LobbySettings from './LobbySettings.vue'
import LockingSettings from './LockingSettings.vue'
import MatterbridgeSettings from './Matterbridge/MatterbridgeSettings.vue'
import MentionsSettings from './MentionsSettings.vue'
import NotificationsSettings from './NotificationsSettings.vue'
import PreserveConversationSettings from './PreserveConversationSettings.vue'
import RecordingConsentSettings from './RecordingConsentSettings.vue'
import SipSettings from './SipSettings.vue'
import { CALL, CONFIG, CONVERSATION, PARTICIPANT } from '../../constants.ts'
import {
	getTalkConfig,
	hasTalkFeature,
	showTalkFeatureHint,
} from '../../services/CapabilitiesManager.ts'
import { useActorStore } from '../../stores/actor.ts'
import { isChannelConversation, isClassifiedConversation } from '../../utils/conversation.ts'

// FIXME Should use remote not local
const matterbridgeEnabled = getTalkConfig('local', 'chat', 'matterbridge-enabled')
const supportsArchive = hasTalkFeature('local', 'archived-conversations-v2')
const supportsPreserve = hasTalkFeature('local', 'preserve-conversation')

export default {
	name: 'ConversationSettingsDialog',

	components: {
		BanSettings,
		BasicInfo,
		BotsSettings,
		BreakoutRoomsSettings,
		ConversationPermissionsSettings,
		DangerZone,
		ExpirationSettings,
		LinkShareSettings,
		ListableSettings,
		LiveTranscriptionSettings,
		LobbySettings,
		LockingSettings,
		MatterbridgeSettings,
		MentionsSettings,
		NcAppSettingsDialog,
		NcAppSettingsSection,
		NcCheckboxRadioSwitch,
		NotificationsSettings,
		PreserveConversationSettings,
		RecordingConsentSettings,
		SipSettings,
	},

	setup() {
		const token = ref('')

		const meetingHeader = t('spreed', 'Meeting') // TRANSLATORS: Section header for meeting-related settings; also a static name fallback for instant meeting conversation

		return {
			matterbridgeEnabled,
			supportsArchive,
			supportsPreserve,
			token,
			meetingHeader,
			actorStore: useActorStore(),
		}
	},

	computed: {
		canUserEnableSIP() {
			return this.conversation.canEnableSIP && !this.isClassified
		},

		isCallEnabled() {
			return getTalkConfig(this.token, 'call', 'enabled')
		},

		isNoteToSelf() {
			return this.conversation.type === CONVERSATION.TYPE.NOTE_TO_SELF
		},

		isOneToOne() {
			return this.conversation.type === CONVERSATION.TYPE.ONE_TO_ONE || this.isOneToOneFormer
		},

		isOneToOneFormer() {
			return this.conversation.type === CONVERSATION.TYPE.ONE_TO_ONE_FORMER
		},

		isGuest() {
			return this.actorStore.isActorGuest
		},

		showSettings() {
			return this.token !== ''
		},

		supportBanV1() {
			return hasTalkFeature(this.token, 'ban-v1')
		},

		conversation() {
			return this.$store.getters.conversation(this.token) || this.$store.getters.dummyConversation
		},

		isArchived() {
			return this.conversation.isArchived
		},

		participantType() {
			return this.conversation.participantType
		},

		selfIsOwnerOrModerator() {
			return (this.participantType === PARTICIPANT.TYPE.OWNER || this.participantType === PARTICIPANT.TYPE.MODERATOR)
		},

		showPreserveConversationSetting() {
			return supportsPreserve && this.canFullModerate
		},

		canFullModerate() {
			return this.selfIsOwnerOrModerator
				&& (this.conversation.type === CONVERSATION.TYPE.GROUP
					|| this.conversation.type === CONVERSATION.TYPE.PUBLIC)
		},

		canDeleteConversation() {
			return this.conversation.canDeleteConversation
		},

		canLeaveConversation() {
			return this.conversation.canLeaveConversation
		},

		isBreakoutRoom() {
			return this.conversation.objectType === CONVERSATION.OBJECT_TYPE.BREAKOUT_ROOM
		},

		supportBotsV1() {
			return hasTalkFeature(this.token, 'bots-v1')
		},

		isLiveTranscriptionSupported() {
			return getTalkConfig(this.token, 'call', 'live-transcription') || false
		},

		canConfigureLiveTranscription() {
			return this.isCallEnabled
				&& this.isLiveTranscriptionSupported
				&& this.selfIsOwnerOrModerator
				&& !this.isClassified
				&& !this.isChannel
		},

		hintLiveTranscription() {
			return this.isCallEnabled
				&& !this.isLiveTranscriptionSupported
				&& this.selfIsOwnerOrModerator
				&& showTalkFeatureHint(34)
				&& !this.isClassified
		},

		canConfigureBreakoutRooms() {
			return !this.isVoiceRoom
				&& this.canFullModerate
				&& (getTalkConfig(this.token, 'call', 'breakout-rooms') || false)
				&& this.conversation.type === CONVERSATION.TYPE.GROUP
				&& !this.isClassified
				&& !this.isChannel
		},

		recordingConsentAvailable() {
			return this.isCallEnabled
				&& (getTalkConfig(this.token, 'call', 'recording') || false)
				&& hasTalkFeature(this.token, 'recording-consent')
				&& getTalkConfig(this.token, 'call', 'recording-consent') !== CONFIG.RECORDING_CONSENT.OFF
				&& !this.isClassified
				&& !this.isChannel
		},

		recordingConsentRequired() {
			return this.conversation.recordingConsent === CALL.RECORDING_CONSENT.ENABLED
		},

		hasMessageExpirationFeature() {
			return hasTalkFeature(this.token, 'message-expiration')
		},

		isVoiceRoom() {
			return Boolean(this.conversation.attributes & CONVERSATION.ATTRIBUTE.VOICE_ROOM)
		},

		isClassified() {
			return isClassifiedConversation(this.conversation)
		},

		isChannel() {
			return isChannelConversation(this.conversation)
		},
	},

	beforeMount() {
		subscribe('show-conversation-settings', this.handleShowSettings)
		subscribe('hide-conversation-settings', this.handleHideSettings)
	},

	beforeUnmount() {
		unsubscribe('show-conversation-settings', this.handleShowSettings)
		unsubscribe('hide-conversation-settings', this.handleHideSettings)
	},

	methods: {
		t,

		/**
		 * Opens ConversationSettingsDialog
		 *
		 * @param payload event payload
		 * @param payload.token conversation token
		 */
		handleShowSettings(payload) {
			this.token = payload.token
		},

		handleHideSettings() {
			this.token = ''
		},

		async toggleArchiveConversation() {
			await this.$store.dispatch('toggleArchive', this.conversation)
		},
	},
}
</script>

<style lang="scss" scoped>
:deep(.app-settings-section__hint) {
	color: var(--color-text-maxcontrast);
	padding: 8px 0;
}

:deep(.app-settings-section__subtitle),
.app-settings-section__subtitle {
	font-weight: bold;
	font-size: var(--default-font-size);
	margin: calc(var(--default-grid-baseline) * 4) 0 var(--default-grid-baseline) 0;
}

:deep(.app-settings-subsection:not(:first-child)) {
	margin-top: 25px;
}
</style>
